import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  CircularProgress,
  Pagination,
  PaginationItem,
  Alert,
} from "@mui/material";
import {
  Search,
  FilterX,
  PlayCircle,
  Download,
  ExternalLink,
  Package,
  HardDrive,
  ArrowLeft,
} from "lucide-react";
import { Link } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import CameraModal from "../components/CameraModel";

const transformArchiveData = (apiResponse) => {
  const dataList = apiResponse?.content || [];
  return dataList.map((item, index) => {
    let dateStr = "--";
    let timeStr = "--";
    if (item.videoDate || item.date) {
      try {
        const d = new Date(item.videoDate || item.date);
        dateStr = d.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
        timeStr = d.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        });
      } catch (e) {}
    }

    return {
      id: item.id || index,
      packageCode: item.packageName || item.packageCode || "Unknown",
      district: item.siteName || item.district || "Unknown",
      assetName: item.assetName || "Asset",
      assetDesc: item.assetDescription || item.description || "--",
      date: dateStr,
      time: timeStr,
      duration: item.duration || "--",
      size: item.size ? `${(item.size / 1024 / 1024).toFixed(2)} MB` : "--",
      type: item.type || (item.isLive ? "Live Recording" : "Uploaded"),
      url: item.videoUrl || item.url || item.s3Url,
    };
  });
};

export default function ArchivePage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [archives, setArchives] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [allPackages, setAllPackages] = useState([]);
  const [totalElements, setTotalElements] = useState(0);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("All Districts");
  const [selectedPackage, setSelectedPackage] = useState("All Packages");
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCameraForModal, setSelectedCameraForModal] = useState(null);

  useEffect(() => {
    const fetchHierarchy = async () => {
      try {
        const hierarchyRes = await axiosInstance.get(
          "/api/site/hierarchy?includeAssets=true"
        );
        const apiData = hierarchyRes.data;

        const loadedDistricts = [];
        const loadedAllPackages = [];

        apiData.forEach((site) => {
          const siteId = site.id.toString();
          loadedDistricts.push({ id: siteId, name: site.name });

          if (site.children && Array.isArray(site.children)) {
            site.children.forEach((childPkg) => {
              loadedAllPackages.push({
                id: childPkg.id.toString(),
                displayName: childPkg.name,
                districtId: siteId,
                districtName: site.name,
              });
            });
          }
        });

        setDistricts(loadedDistricts);
        setAllPackages(loadedAllPackages);
      } catch (error) {
        console.error("Failed to load hierarchy:", error);
      }
    };

    fetchHierarchy();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchArchives = async () => {
      setLoading(true);
      setError(null);
      try {
        const dateObj = new Date(selectedDate);
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, "0");
        const dateParam = `${year}-${month}`;

        const res = await axiosInstance.post(
          `/api/pre-check/archive-report?date=${dateParam}`,
          {
            page: page - 1,
            size: rowsPerPage,
          }
        );

        if (isMounted) {
          setArchives(transformArchiveData(res.data));
          setTotalElements(res.data.totalElements || res.data.total || 0);
        }
      } catch (err) {
        console.error("Archive API Error:", err);
        if (isMounted) setError("Failed to load archive data.");
        setArchives([]);
        setTotalElements(0);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchArchives();

    return () => {
      isMounted = false;
    };
  }, [page, rowsPerPage, selectedDate]);

  const filteredArchives = useMemo(() => {
    return archives.filter((item) => {
      const matchesSearch =
        searchTerm === "" ||
        item.packageCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.assetName.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesDistrict =
        selectedDistrict === "All Districts" ||
        item.district === selectedDistrict ||
        districts.find((d) => d.id === selectedDistrict)?.name ===
          item.district;

      const matchesPackage =
        selectedPackage === "All Packages" ||
        item.packageCode === selectedPackage ||
        allPackages.find((p) => p.id === selectedPackage)?.displayName ===
          item.packageCode;

      return matchesSearch && matchesDistrict && matchesPackage;
    });
  }, [
    archives,
    searchTerm,
    selectedDistrict,
    selectedPackage,
    districts,
    allPackages,
  ]);

  const paginatedArchives = filteredArchives;
  const totalPages = Math.ceil(totalElements / rowsPerPage);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setSelectedDistrict("All Districts");
    setSelectedPackage("All Packages");
    setSelectedDate(new Date().toISOString().split("T")[0]);
    setPage(1);
  };

  const getStatusColor = (type) => {
    if (type === "Live Recording") return { bg: "#DCFCE7", color: "#166534" };
    return { bg: "#DBEAFE", color: "#1E40AF" };
  };

  const handlePlay = (item) => {
    if (!item.url) return alert("Video URL not available");
    setSelectedCameraForModal({ camera_id: item.assetName, url: item.url });
    setModalOpen(true);
  };

  const handleDownload = (item) => {
    if (!item.url) return alert("No URL to download");
    const link = document.createElement("a");
    link.href = item.url;
    link.download = item.assetName || "video";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenInNewTab = (url) => {
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <Box
        sx={{
          p: 3,
          mt: 0,
          bgcolor: "#F8FAFC",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Typography
            component={Link}
            to="/"
            sx={{
              color: "#64748B",
              fontSize: "14px",
              display: "inline-flex",
              alignItems: "center",
              textDecoration: "none",
              "&:hover": { color: "#1E293B" },
            }}
          >
            <ArrowLeft size={16} style={{ marginRight: 4 }} /> Back to Dashboard
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
            <HardDrive size={24} color="#2563EB" />
            <Typography variant="h5" sx={{ fontWeight: 700, color: "#1E293B" }}>
              Video Archive
            </Typography>
            <Chip
              label={`${totalElements} files found`}
              size="small"
              sx={{
                bgcolor: "#fff",
                border: "1px solid #E2E8F0",
                fontWeight: 600,
              }}
            />
          </Box>
          <Typography variant="body2" color="textSecondary">
            Browse and search uploaded and live video history
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            border: "1px solid #E2E8F0",
            borderRadius: 2,
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <TextField
            placeholder="Search packages, assets..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1, minWidth: "250px", bgcolor: "#fff" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} color="#94A3B8" />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select
              value={selectedDistrict}
              onChange={(e) => setSelectedDistrict(e.target.value)}
              displayEmpty
              sx={{ bgcolor: "#fff" }}
            >
              <MenuItem value="All Districts">All Districts</MenuItem>
              {districts.map((d) => (
                <MenuItem key={d.id} value={d.name}>
                  {d.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select
              value={selectedPackage}
              onChange={(e) => setSelectedPackage(e.target.value)}
              displayEmpty
              sx={{ bgcolor: "#fff" }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
            >
              <MenuItem value="All Packages">All Packages</MenuItem>
              {allPackages.map((p) => (
                <MenuItem key={p.id} value={p.displayName}>
                  {p.displayName}{" "}
                  <Typography
                    component="span"
                    variant="caption"
                    color="textSecondary"
                    sx={{ ml: 1 }}
                  >
                    ({p.districtName})
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            type="date"
            size="small"
            value={selectedDate}
            onChange={(e) => {
              setSelectedDate(e.target.value);
              setPage(1);
            }}
            sx={{ bgcolor: "#fff" }}
          />

          <Button
            variant="text"
            startIcon={<FilterX size={16} />}
            onClick={handleClearFilters}
            sx={{ textTransform: "none", color: "#64748B" }}
          >
            Clear Filters
          </Button>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: "1px solid #E2E8F0",
            borderRadius: 2,
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Table sx={{ minWidth: 650 }} stickyHeader>
            <TableHead sx={{ bgcolor: "#F8FAFC" }}>
              <TableRow>
                {[
                  "PACKAGE",
                  "ASSET",
                  "DATE",
                  "TIME",
                  "DURATION",
                  "SIZE",
                  "TYPE",
                  "ACTIONS",
                ].map((head) => (
                  <TableCell
                    key={head}
                    sx={{ fontWeight: 600, color: "#64748B", fontSize: 12 }}
                  >
                    {head}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : paginatedArchives.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <Typography variant="body1" color="textSecondary">
                      No records found matching your filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedArchives.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                      >
                        <Box
                          sx={{
                            p: 1,
                            bgcolor: "#EFF6FF",
                            borderRadius: 1,
                            color: "#3B82F6",
                          }}
                        >
                          <Package size={18} />
                        </Box>
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600 }}
                          >
                            {row.packageCode}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {row.district}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {row.assetName}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          sx={{ fontSize: 11 }}
                        >
                          {row.assetDesc}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: "#475569", fontSize: 13 }}>
                      {row.date}
                    </TableCell>
                    <TableCell sx={{ color: "#475569", fontSize: 13 }}>
                      {row.time}
                    </TableCell>
                    <TableCell sx={{ color: "#475569", fontSize: 13 }}>
                      {row.duration}
                    </TableCell>
                    <TableCell sx={{ color: "#475569", fontSize: 13 }}>
                      {row.size}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.type}
                        size="small"
                        sx={{
                          bgcolor: getStatusColor(row.type).bg,
                          color: getStatusColor(row.type).color,
                          fontWeight: 600,
                          fontSize: 11,
                          height: 24,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title="Play">
                          <IconButton
                            size="small"
                            sx={{ color: "#2563EB" }}
                            onClick={() => handlePlay(row)}
                          >
                            <PlayCircle size={18} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Open in New Tab">
                          <IconButton
                            size="small"
                            sx={{ color: "#64748B" }}
                            onClick={() => handleOpenInNewTab(row.url)}
                          >
                            <ExternalLink size={18} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download">
                          <IconButton
                            size="small"
                            sx={{ color: "#64748B" }}
                            onClick={() => handleDownload(row)}
                          >
                            <Download size={18} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2,
              borderTop: "1px solid #E2E8F0",
              bgcolor: "#fff",
            }}
          >
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ fontSize: "13px" }}
            >
              Showing {(page - 1) * rowsPerPage + 1} to{" "}
              {Math.min(page * rowsPerPage, totalElements)} of {totalElements}{" "}
              results
            </Typography>

            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              variant="outlined"
              shape="rounded"
              color="primary"
              size="medium"
              showFirstButton
              showLastButton
              renderItem={(item) => (
                <PaginationItem
                  {...item}
                  sx={{
                    borderRadius: 1,
                    fontWeight: 500,
                    fontSize: "13px",
                    "&.Mui-selected": {
                      bgcolor: "#2563EB",
                      color: "white",
                      border: "none",
                      "&:hover": {
                        bgcolor: "#1D4ED8",
                      },
                    },
                  }}
                />
              )}
            />
          </Box>
        </TableContainer>
      </Box>

      <CameraModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        selectedCamera={selectedCameraForModal}
      />
    </>
  );
}
